import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@arcadeai/design-system/components/ui/atoms/table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Plus,
  Search,
  Settings2,
  Trash2,
} from "lucide-react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";

export const ApiKeysListSkeleton = () => {
  return (
    <ListLayout
      actions={
        <Button disabled>
          <Plus />
          Create API Key
        </Button>
      }
      description="Manage the API keys for this project. Create new keys to securely access Arcade's features, view existing ones, or revoke access when needed."
      title="API Keys"
    >
      <div className="my-4 space-y-4">
        {/* Toolbar skeleton */}
        <div className="flex items-center gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute top-2 left-2 h-4 w-4 text-muted-foreground" />
            <Input
              className="h-8 pl-8"
              disabled
              placeholder="Search by label..."
            />
          </div>
          <Button
            className="hidden h-8 lg:flex"
            disabled
            size="sm"
            variant="outline"
          >
            <Settings2 />
            Columns
          </Button>
        </div>

        {/* Table skeleton */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <div className="flex items-center space-x-2">
                    <span>Label</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center space-x-2">
                    <span>Preview</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center space-x-2">
                    <span>Created At</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }, () => (
                <ApiKeyRowSkeleton key={crypto.randomUUID()} />
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination skeleton */}
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <p className="font-medium text-sm">Rows per page</p>
            <Select disabled>
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder="10" />
              </SelectTrigger>
              <SelectContent side="top">
                <SelectItem value="10">10</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Page 1 of <Skeleton className="ml-1 inline-block h-4 w-6" />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                className="hidden h-8 w-8 p-0 lg:flex"
                disabled
                variant="outline"
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button className="h-8 w-8 p-0" disabled variant="outline">
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button className="h-8 w-8 p-0" disabled variant="outline">
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                className="hidden h-8 w-8 p-0 lg:flex"
                disabled
                variant="outline"
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ListLayout>
  );
};

const ApiKeyRowSkeleton = () => {
  return (
    <TableRow>
      {/* Label */}
      <TableCell>
        <Skeleton className="h-4 w-24" />
      </TableCell>

      {/* Preview */}
      <TableCell>
        <Skeleton className="h-4 w-32" />
      </TableCell>

      {/* Created At */}
      <TableCell>
        <Skeleton className="h-4 w-20" />
      </TableCell>

      {/* Actions */}
      <TableCell>
        <Button
          className="text-destructive/70"
          disabled
          size="icon"
          variant="ghost"
        >
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};
